import Button from 'devextreme-react/button';
import DateBox from 'devextreme-react/date-box';
import LoadIndicator from 'devextreme-react/load-indicator';
import SelectBox from 'devextreme-react/select-box';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildReportingRangePresetGroups,
  defaultCustomRange,
  parseLocalDateKey,
  rangePresetToQuery,
  toLocalDateKey,
  type ReportingRangePreset
} from '../analytics/reportingRange';
import { useChartThemeColors } from '../charts/useChartThemeColors';
import { showErrorToast } from '../notifications/toast';
import { AccessReport } from './reports/AccessReport';
import { GeoReport } from './reports/GeoReport';
import { SummaryReport } from './reports/SummaryReport';
import { TechnologyReport } from './reports/TechnologyReport';
import { TrendsReport } from './reports/TrendsReport';
import { UsersReport } from './reports/UsersReport';
import { UserActivityHistoryGrid } from './UserActivityHistoryGrid';
import {
  isUserActivityPageTab,
  loadUserActivityUiPrefs,
  normalizeUserActivityPageTab,
  saveUserActivityUiPrefs,
  type UserActivityAccessSubview,
  type UserActivityGeoSubview,
  type UserActivityTechnologySubview,
  type UserActivityTrendsSubview,
  type UserActivityUsersMetric
} from './userActivityPrefs';
import type {
  UserActivityAnalytics,
  UserActivityAnalyticsPageProps,
  UserActivityAnalyticsSectionName,
  UserActivityLive,
  UserActivityPageTab,
  UserActivityReportMetric,
  UserActivitySessionFilter
} from './types';

const DEFAULT_PREFS_KEY = 'platform-user-activity-ui';
const LIVE_POLL_MS = 5_000;

type AnalyticsCacheKey = string;

function sectionForTab(tab: UserActivityPageTab): UserActivityAnalyticsSectionName | null {
  if (tab === 'sessions') return null;
  if (tab === 'summary') return 'summary';
  if (tab === 'trends') return 'trends';
  if (tab === 'users') return 'users';
  if (tab === 'access') return 'access';
  if (tab === 'technology') return 'technology';
  if (tab === 'geo') return 'geo';
  return null;
}

function cacheKey(section: string, from?: string, to?: string, topN?: number): AnalyticsCacheKey {
  return `${section}|${from ?? ''}|${to ?? ''}|${topN ?? ''}`;
}

export function UserActivityAnalyticsPage({
  api,
  stateStorageKey,
  showTenantColumn = false,
  prefsStorageKey = DEFAULT_PREFS_KEY,
  initialTab,
  onTabChange
}: UserActivityAnalyticsPageProps) {
  const chartColors = useChartThemeColors();
  const analyticsGenRef = useRef(0);
  const liveGenRef = useRef(0);
  const cacheRef = useRef<Map<AnalyticsCacheKey, UserActivityAnalytics>>(new Map());
  const initial = loadUserActivityUiPrefs(prefsStorageKey);

  const resolvedInitialTab =
    normalizeUserActivityPageTab(initialTab) ??
    normalizeUserActivityPageTab(initial.tab) ??
    'summary';

  const [tab, setTab] = useState<UserActivityPageTab>(resolvedInitialTab);
  const [trendsSubview, setTrendsSubview] = useState<UserActivityTrendsSubview>(initial.trendsSubview);
  const [accessSubview, setAccessSubview] = useState<UserActivityAccessSubview>(initial.accessSubview);
  const [technologySubview, setTechnologySubview] = useState<UserActivityTechnologySubview>(
    initial.technologySubview
  );
  const [geoSubview, setGeoSubview] = useState<UserActivityGeoSubview>(initial.geoSubview);
  const [reportMetric, setReportMetric] = useState<UserActivityReportMetric>(initial.reportMetric);
  const [usersMetric, setUsersMetric] = useState<UserActivityUsersMetric>(initial.usersMetric);
  const [topN, setTopN] = useState(initial.topN);
  const [rangePreset, setRangePreset] = useState<ReportingRangePreset>(initial.rangePreset);
  const [customFrom, setCustomFrom] = useState<string | null>(initial.customFrom);
  const [customTo, setCustomTo] = useState<string | null>(initial.customTo);

  const [live, setLive] = useState<UserActivityLive | null>(null);
  const [analytics, setAnalytics] = useState<UserActivityAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [liveBusy, setLiveBusy] = useState(false);
  const [sessionsFilter, setSessionsFilter] = useState<UserActivitySessionFilter | null>(null);
  const [sessionsFilterLabel, setSessionsFilterLabel] = useState<string | null>(null);

  const customRange = useMemo(
    () => ({ from: customFrom, to: customTo }),
    [customFrom, customTo]
  );
  const rangeQuery = useMemo(
    () => rangePresetToQuery(rangePreset, new Date(), customRange),
    [rangePreset, customRange]
  );
  const rangePresetGroups = useMemo(() => buildReportingRangePresetGroups(), []);
  const rangeKey = `${rangeQuery.from ?? ''}|${rangeQuery.to ?? ''}`;

  useEffect(() => {
    const next = normalizeUserActivityPageTab(initialTab);
    if (next && next !== tab) setTab(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync URL deep-link only when prop changes
  }, [initialTab]);

  useEffect(() => {
    saveUserActivityUiPrefs(prefsStorageKey, {
      tab,
      trendsSubview,
      accessSubview,
      technologySubview,
      geoSubview,
      reportMetric,
      usersMetric,
      topN,
      rangePreset,
      customFrom,
      customTo
    });
    onTabChange?.(tab);
  }, [
    prefsStorageKey,
    tab,
    trendsSubview,
    accessSubview,
    technologySubview,
    geoSubview,
    reportMetric,
    usersMetric,
    topN,
    rangePreset,
    customFrom,
    customTo,
    onTabChange
  ]);

  // Clear analytics cache when range changes.
  useEffect(() => {
    cacheRef.current.clear();
  }, [rangeKey, topN]);

  const loadLive = useCallback(
    async (opts?: { quiet?: boolean }) => {
      const gen = ++liveGenRef.current;
      if (opts?.quiet) setLiveBusy(true);
      try {
        const next = await api.getLive();
        if (gen !== liveGenRef.current) return;
        setLive(next);
      } catch (err) {
        if (gen !== liveGenRef.current) return;
        if (!opts?.quiet) {
          showErrorToast(err instanceof Error ? err.message : 'Failed to load live activity.');
        }
      } finally {
        if (gen === liveGenRef.current) setLiveBusy(false);
      }
    },
    [api]
  );

  const loadAnalytics = useCallback(
    async (section: UserActivityAnalyticsSectionName) => {
      const key = cacheKey(section, rangeQuery.from, rangeQuery.to, topN);
      const cached = cacheRef.current.get(key);
      if (cached) {
        setAnalytics(cached);
        setLoading(false);
        return;
      }

      const gen = ++analyticsGenRef.current;
      setLoading(true);
      try {
        const next = await api.getAnalytics({
          ...rangeQuery,
          section,
          topN
        });
        if (gen !== analyticsGenRef.current) return;
        cacheRef.current.set(key, next);
        setAnalytics(next);
      } catch (err) {
        if (gen !== analyticsGenRef.current) return;
        showErrorToast(err instanceof Error ? err.message : 'Failed to load activity analytics.');
      } finally {
        if (gen === analyticsGenRef.current) setLoading(false);
      }
    },
    [api, rangeQuery, topN]
  );

  useEffect(() => {
    const section = sectionForTab(tab);
    if (!section) {
      setLoading(false);
      return;
    }
    void loadAnalytics(section);
  }, [tab, loadAnalytics]);

  useEffect(() => {
    if (tab !== 'summary') return;
    void loadLive();
    const timer = window.setInterval(() => void loadLive({ quiet: true }), LIVE_POLL_MS);
    return () => window.clearInterval(timer);
  }, [tab, loadLive]);

  const selectTab = (next: UserActivityPageTab) => setTab(next);

  const onRangePresetChange = (next: ReportingRangePreset) => {
    setRangePreset(next);
    if (next === 'custom' && (!customFrom || !customTo)) {
      const defaults = defaultCustomRange();
      setCustomFrom(defaults.from);
      setCustomTo(defaults.to);
    }
  };

  const openSessionsDrilldown = (label: string, filter: unknown) => {
    setSessionsFilterLabel(label);
    setSessionsFilter(filter);
    setTab('sessions');
  };

  const clearSessionsFilter = () => {
    setSessionsFilter(null);
    setSessionsFilterLabel(null);
  };

  const refreshCurrent = () => {
    cacheRef.current.clear();
    const section = sectionForTab(tab);
    if (tab === 'summary') void loadLive();
    if (section) void loadAnalytics(section);
  };

  const reportingTimeZone = analytics?.reportingTimeZone ?? live?.reportingTimeZone ?? 'Eastern Time';

  return (
    <div className="ops-fill-page user-activity-analytics">
      <div className="user-activity-analytics__toolbar">
        <div className="user-activity-analytics__tabs" role="tablist" aria-label="User activity views">
          <Button
            icon="chart"
            stylingMode={tab === 'summary' ? 'contained' : 'text'}
            type={tab === 'summary' ? 'default' : 'normal'}
            hint="Summary"
            aria-label="Summary"
            onClick={() => selectTab('summary')}
          />
          <Button
            icon="clock"
            stylingMode={tab === 'trends' ? 'contained' : 'text'}
            type={tab === 'trends' ? 'default' : 'normal'}
            hint="Trends"
            aria-label="Trends"
            onClick={() => selectTab('trends')}
          />
          <Button
            icon="group"
            stylingMode={tab === 'users' ? 'contained' : 'text'}
            type={tab === 'users' ? 'default' : 'normal'}
            hint="Users"
            aria-label="Users"
            onClick={() => selectTab('users')}
          />
          <Button
            icon="key"
            stylingMode={tab === 'access' ? 'contained' : 'text'}
            type={tab === 'access' ? 'default' : 'normal'}
            hint="Access"
            aria-label="Access"
            onClick={() => selectTab('access')}
          />
          <Button
            icon="toolbox"
            stylingMode={tab === 'technology' ? 'contained' : 'text'}
            type={tab === 'technology' ? 'default' : 'normal'}
            hint="Technology"
            aria-label="Technology"
            onClick={() => selectTab('technology')}
          />
          <Button
            icon="globe"
            stylingMode={tab === 'geo' ? 'contained' : 'text'}
            type={tab === 'geo' ? 'default' : 'normal'}
            hint="Geo & network"
            aria-label="Geo and network"
            onClick={() => selectTab('geo')}
          />
          <Button
            icon="detailslayout"
            stylingMode={tab === 'sessions' ? 'contained' : 'text'}
            type={tab === 'sessions' ? 'default' : 'normal'}
            hint="Sessions"
            aria-label="Sessions"
            onClick={() => selectTab('sessions')}
          />
        </div>

        <div
          className={[
            'user-activity-analytics__filters',
            rangePreset === 'custom' ? 'user-activity-analytics__filters--custom' : ''
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div className="user-activity-analytics__filter user-activity-analytics__filter--range">
            <SelectBox
              dataSource={rangePresetGroups}
              grouped
              value={rangePreset}
              valueExpr="value"
              displayExpr="text"
              width="100%"
              hint="Date range"
              onValueChanged={(e) => onRangePresetChange(e.value as ReportingRangePreset)}
            />
          </div>
          {rangePreset === 'custom' ? (
            <div className="user-activity-analytics__filter user-activity-analytics__filter--custom-dates">
              <DateBox
                type="date"
                value={parseLocalDateKey(customFrom)}
                max={parseLocalDateKey(customTo) ?? undefined}
                onValueChanged={(e) => {
                  const d = e.value instanceof Date ? e.value : null;
                  setCustomFrom(d ? toLocalDateKey(d) : null);
                }}
              />
              <span className="user-activity-analytics__date-sep">–</span>
              <DateBox
                type="date"
                value={parseLocalDateKey(customTo)}
                min={parseLocalDateKey(customFrom) ?? undefined}
                onValueChanged={(e) => {
                  const d = e.value instanceof Date ? e.value : null;
                  setCustomTo(d ? toLocalDateKey(d) : null);
                }}
              />
            </div>
          ) : null}
          {tab !== 'summary' && tab !== 'sessions' && tab !== 'trends' && tab !== 'users' ? (
            <SelectBox
              dataSource={[8, 12, 20, 30]}
              value={topN}
              width={90}
              hint="Top N"
              displayExpr={(v) => `Top ${v}`}
              onValueChanged={(e) => setTopN(Number(e.value))}
            />
          ) : null}
        </div>

        <Button
          icon="refresh"
          stylingMode="text"
          hint="Refresh"
          aria-label="Refresh"
          disabled={loading || liveBusy}
          onClick={() => refreshCurrent()}
        />
      </div>

      {loading && tab !== 'sessions' && !analytics ? (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '1rem' }}>
          <LoadIndicator visible height={20} width={20} />
          <span>Loading user activity…</span>
        </div>
      ) : null}

      {tab === 'summary' && analytics?.summary ? (
        <SummaryReport
          live={live}
          summary={analytics.summary}
          reportingTimeZone={reportingTimeZone}
          chartColors={chartColors}
          liveBusy={liveBusy}
          onRefreshLive={() => void loadLive({ quiet: true })}
          showTenantColumn={showTenantColumn}
        />
      ) : null}

      {tab === 'trends' && analytics?.trends ? (
        <TrendsReport
          trends={analytics.trends}
          reportingTimeZone={reportingTimeZone}
          chartColors={chartColors}
          subview={trendsSubview}
          onSubviewChange={setTrendsSubview}
          metric={reportMetric}
          onMetricChange={setReportMetric}
        />
      ) : null}

      {tab === 'users' && analytics?.users ? (
        <UsersReport
          users={analytics.users}
          chartColors={chartColors}
          metric={usersMetric}
          onMetricChange={setUsersMetric}
          showTenantColumn={showTenantColumn}
          onDrilldown={openSessionsDrilldown}
        />
      ) : null}

      {tab === 'access' && analytics?.access ? (
        <AccessReport
          access={analytics.access}
          chartColors={chartColors}
          subview={accessSubview}
          onSubviewChange={setAccessSubview}
          metric={reportMetric}
          onMetricChange={setReportMetric}
          showTenantColumn={showTenantColumn}
          onDrilldown={openSessionsDrilldown}
        />
      ) : null}

      {tab === 'technology' && analytics?.technology ? (
        <TechnologyReport
          technology={analytics.technology}
          chartColors={chartColors}
          subview={technologySubview}
          onSubviewChange={setTechnologySubview}
          metric={reportMetric}
          onMetricChange={setReportMetric}
          onDrilldown={openSessionsDrilldown}
        />
      ) : null}

      {tab === 'geo' && analytics?.geo ? (
        <GeoReport
          geo={analytics.geo}
          chartColors={chartColors}
          subview={geoSubview}
          onSubviewChange={setGeoSubview}
          metric={reportMetric}
          onMetricChange={setReportMetric}
          onDrilldown={openSessionsDrilldown}
        />
      ) : null}

      {tab === 'sessions' ? (
        <div className="user-activity-analytics__body user-activity-analytics__body--sessions">
          {sessionsFilterLabel ? (
            <div className="user-activity-sessions-filter-bar">
              <span>
                Filtered from report: <strong>{sessionsFilterLabel}</strong>
              </span>
              <Button text="Clear filter" stylingMode="outlined" onClick={clearSessionsFilter} />
            </div>
          ) : null}
          <UserActivityHistoryGrid
            loadPage={(request) => api.listSessions(request)}
            stateStorageKey={stateStorageKey}
            showTenantColumn={showTenantColumn}
            rangeFrom={rangeQuery.from}
            rangeTo={rangeQuery.to}
            initialFilter={sessionsFilter}
            filterNonce={sessionsFilterLabel ?? ''}
          />
        </div>
      ) : null}
    </div>
  );
}

// Re-export helper for hosts that still import tab guards from page module paths.
export { isUserActivityPageTab, normalizeUserActivityPageTab };
